import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  TextField,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  CircularProgress,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import TextFieldsOutlinedIcon from "@mui/icons-material/TextFieldsOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import {
  saveGame,
  loadVideoScene,
  loadSceneImage,
} from "../../redux/asyncThunk/userActions";
import { userSlice } from "../../redux/user/userSlice";
import { useSceneScreenshot } from "../nodes/useSceneScreenshot";
import InputFileUpload from "../UploadFile";
import ConfirmDeleteModal from "../ConfirmDeleteModal";
import Settings from "../Settings";
import ScenarioButtonDeleteDialog from "../ScenarioButtonDeleteDialog";
import {
  getButtonDeletionInfo,
  removeScenarioButton,
} from "../../utils/scenarioDeletion";
import {
  buildScenarioModePatch,
  inferScenarioContentMode,
} from "../../utils/contentMode";
import {
  getScenarioSceneButtons,
  getScenarioSceneNodes,
} from "../../utils/scenarioScenes";
import {
  getButtonPosition,
  getButtonStyle,
  getGroupStyle,
  getNextSceneGroupPosition,
  getSceneStyle,
  separateOverlappingSceneGroups,
  SCHEME_SCENE,
} from "../nodes/schemeLayout";
import "./mobile-editor.css";

const { addMessage } = userSlice;
const MAX_SCENARIO_BUTTONS = 2;

export default function MobileScenarioEditor({ game: initialGame }) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user?.data?.user);
  const storedGame = useSelector((state) => state.user?.game);
  const game =
    storedGame && String(storedGame._id) === String(initialGame?._id)
      ? storedGame
      : initialGame;

  const [nodes, setNodes] = useState(game?.nodes ?? []);
  const [edges, setEdges] = useState(game?.edges ?? []);

  useEffect(() => {
    if (!game) return;
    setNodes(separateOverlappingSceneGroups(game.nodes ?? []));
    setEdges(game.edges ?? []);
  }, [game]);
  const [currentSceneIndex, setCurrentSceneIndex] = useState(0);
  const [editingButtonId, setEditingButtonId] = useState(null);
  const [editingButtonText, setEditingButtonText] = useState("");
  const [activeView, setActiveView] = useState("scenes");
  const [showButtonDialog, setShowButtonDialog] = useState(false);
  const [showVideoDialog, setShowVideoDialog] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [showEditButtonDialog, setShowEditButtonDialog] = useState(false);
  const [isLoadingVideo, setIsLoadingVideo] = useState(false);
  const [videoUploadError, setVideoUploadError] = useState("");
  const [videoPreviewError, setVideoPreviewError] = useState(false);
  const [imagePreviewError, setImagePreviewError] = useState(false);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [buttonDeleteInfo, setButtonDeleteInfo] = useState(null);
  const saveTimerRef = useRef(null);
  const lastSavedRef = useRef(null);

  // Include existing scenes even if a legacy saved node is missing its type.
  const scenes = getScenarioSceneNodes(nodes);
  const currentScene = scenes[currentSceneIndex] || null;
  const currentSceneMode = currentScene
    ? inferScenarioContentMode(currentScene.data)
    : "video";
  const {
    hasScreenshot,
    imageSrc,
    hasImagePreview,
    imagePreviewSrc,
    imageKey,
  } = useSceneScreenshot(currentScene?.id, currentScene?.data);

  useEffect(() => {
    setVideoPreviewError(false);
  }, [imageSrc, imageKey]);

  useEffect(() => {
    setImagePreviewError(false);
  }, [imagePreviewSrc, imageKey]);

  const sceneButtons = getScenarioSceneButtons(currentScene, nodes, edges);

  const handleNavigateToScene = (sceneId) => {
    const targetSceneIndex = scenes.findIndex(
      (scene) => String(scene.id) === String(sceneId),
    );
    if (targetSceneIndex >= 0) {
      setCurrentSceneIndex(targetSceneIndex);
    }
  };

  // Auto-save
  useEffect(() => {
    if (!game?._id) return;

    const data = JSON.stringify({ nodes, edges });
    if (data === lastSavedRef.current) return;

    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      dispatch(saveGame({ selectedGameId: game._id, nodes, edges }))
        .unwrap()
        .then(() => {
          lastSavedRef.current = data;
        })
        .catch(() => {
          dispatch(addMessage({ type: "error", message: "Ошибка сохранения" }));
        });
    }, 1500);

    return () => clearTimeout(saveTimerRef.current);
  }, [nodes, edges, game?._id, dispatch]);

  const handleAddButton = () => {
    if (!currentScene) return;
    if (sceneButtons.length >= MAX_SCENARIO_BUTTONS) {
      dispatch(
        addMessage({
          type: "error",
          message: "Сейчас вы можете добавить только две кнопки",
        }),
      );
      return;
    }
    setEditingButtonId(null);
    setEditingButtonText("");
    setShowButtonDialog(true);
  };

  const handleSaveButton = () => {
    if (!editingButtonText.trim() || !currentScene) {
      dispatch(addMessage({ type: "error", message: "Введите текст кнопки" }));
      return;
    }
    if (!editingButtonId && sceneButtons.length >= MAX_SCENARIO_BUTTONS) {
      dispatch(
        addMessage({
          type: "error",
          message: "Сейчас вы можете добавить только две кнопки",
        }),
      );
      return;
    }

    const buttonId = editingButtonId || `${currentScene.id}-${Date.now()}`;
    const updatedNodes = [...nodes];
    const updatedEdges = [...edges];

    if (!editingButtonId) {
      const buttonPosition = sceneButtons.some(
        (button) => button.buttonPosiiton === "top",
      )
        ? "bottom"
        : "top";

      updatedNodes.push({
        id: buttonId,
        type: "input",
        data: {
          label: editingButtonText,
          buttonPosiiton: buttonPosition,
          isCorrect: false,
          explanation: "",
        },
        position: getButtonPosition(buttonPosition),
        className: "scheme-button",
        style: getButtonStyle(),
        parentId: `${currentScene.id}-group`,
        parentNode: `${currentScene.id}-group`,
        sourcePosition: "right",
        extent: "parent",
        draggable: false,
      });
    } else {
      const nodeIndex = updatedNodes.findIndex((n) => n.id === buttonId);
      if (nodeIndex !== -1) {
        updatedNodes[nodeIndex] = {
          ...updatedNodes[nodeIndex],
          data: { ...updatedNodes[nodeIndex].data, label: editingButtonText },
        };
      }
    }

    setNodes(updatedNodes);
    setEdges(updatedEdges);
    setShowButtonDialog(false);
    setEditingButtonText("");
  };

  const handleDeleteButton = (buttonId, deleteTargetScene = false) => {
    const updated = removeScenarioButton(
      buttonId,
      nodes,
      edges,
      deleteTargetScene,
    );
    setNodes(updated.nodes);
    setEdges(updated.edges);
    setButtonDeleteInfo(null);
  };

  const requestDeleteButton = (buttonId) => {
    const deletionInfo = getButtonDeletionInfo(buttonId, nodes, edges);
    if (deletionInfo.isLinked) {
      setButtonDeleteInfo(deletionInfo);
      return;
    }
    handleDeleteButton(buttonId);
  };

  const handleDeleteScene = () => {
    if (!currentScene) return;

    const sceneId = String(currentScene.id);
    if (sceneId === "1") {
      dispatch(
        addMessage({
          type: "error",
          message: "Нельзя удалить стартовую сцену",
        }),
      );
      return;
    }

    const groupId = String(
      currentScene.parentId ??
        currentScene.parentNode ??
        `${sceneId}-group`,
    );
    const removedNodeIds = new Set([sceneId, groupId]);
    nodes.forEach((node) => {
      const parentId = node.parentId ?? node.parentNode;
      if (String(parentId ?? "") === groupId) {
        removedNodeIds.add(String(node.id));
      }
    });
    getScenarioSceneButtons(currentScene, nodes, edges).forEach((button) => {
      removedNodeIds.add(String(button.buttonId));
    });

    const updatedNodes = nodes.filter(
      (node) => !removedNodeIds.has(String(node.id)),
    );
    const updatedEdges = edges.filter(
      (edge) =>
        !removedNodeIds.has(String(edge.source)) &&
        !removedNodeIds.has(String(edge.target)),
    );

    setNodes(updatedNodes);
    setEdges(updatedEdges);
    setShowDeleteConfirm(null);

    if (currentSceneIndex > 0) {
      setCurrentSceneIndex(currentSceneIndex - 1);
    }
  };

  const handleCreateSceneFromButton = (buttonId) => {
    const button = nodes.find((node) => String(node.id) === String(buttonId));
    if (button?.type !== "input") return;

    if (edges.some((edge) => String(edge.source) === String(buttonId))) {
      return;
    }

    const newSceneId = Date.now().toString();
    const groupId = `${newSceneId}-group`;
    const parentGroupId = button.parentId ?? button.parentNode;
    const parentGroup = nodes.find(
      (node) => String(node.id) === String(parentGroupId),
    );
    const groupPosition = getNextSceneGroupPosition(nodes, parentGroup);
    const groupNode = {
      id: groupId,
      type: "group",
      className: "scheme-group",
      data: { label: `Сцена ${newSceneId}` },
      dragHandle: ".scheme-group-drag-handle",
      position: groupPosition,
      style: getGroupStyle(),
    };
    const sceneNode = {
      id: newSceneId,
      type: "node-with-toolbar",
      data: {
        label: `Сцена ${newSceneId}`,
        sceneMode: "video",
        sceneText: "",
      },
      position: { x: SCHEME_SCENE.x, y: SCHEME_SCENE.y },
      targetPosition: "left",
      className: "scheme-scene",
      style: getSceneStyle(),
      extent: "parent",
      parentNode: groupId,
      parentId: groupId,
      draggable: false,
    };
    const edge = {
      id: String(buttonId),
      source: String(buttonId),
      sourceHandle: "source",
      target: newSceneId,
      type: "step",
    };

    setNodes([...nodes, groupNode, sceneNode]);
    setEdges([...edges, edge]);
    setCurrentSceneIndex(scenes.length);
  };

  const handleContentModeChange = (mode) => {
    if (!currentScene || !mode) return;

    const patch = buildScenarioModePatch(mode);
    const updatedNodes = nodes.map((node) => {
      if (node.id === currentScene.id) {
        return {
          ...node,
          data: { ...node.data, ...patch },
        };
      }
      return node;
    });

    setNodes(updatedNodes);
  };

  const handleVideoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setVideoUploadError("");
    if (!currentScene || !game?._id || !user?.id) {
      const message = "Не удалось определить проект или сцену для загрузки видео";
      setVideoUploadError(message);
      dispatch(addMessage({ type: "error", message }));
      event.target.value = "";
      return;
    }

    setIsLoadingVideo(true);
    try {
      const result = await dispatch(
        loadVideoScene({
          userId: user.id,
          selectedGameId: game._id,
          scene: String(currentScene.id),
          file,
          game: { ...game, nodes, edges },
        }),
      ).unwrap();

      const savedScene = result?.nodes?.find(
        (node) => String(node.id) === String(currentScene.id),
      );
      if (!savedScene?.data?.hasVideo || !savedScene.data.screenpath) {
        throw new Error("Сервер не подтвердил загрузку видео в эту сцену");
      }

      setNodes((currentNodes) =>
        currentNodes.map((node) =>
          String(node.id) === String(currentScene.id)
            ? {
                ...node,
                data: {
                  ...node.data,
                  hasVideo: true,
                  video: savedScene.data.video ?? "X",
                  videopath: savedScene.data.videopath ?? "",
                  screenpath: savedScene.data.screenpath,
                  sceneMode: "video",
                },
              }
            : node,
        ),
      );
      setShowVideoDialog(false);
    } catch (error) {
      const message =
        error?.message ||
        error?.payload?.message ||
        "Не удалось загрузить видео. Попробуйте ещё раз.";
      setVideoUploadError(message);
      dispatch(addMessage({ type: "error", message }));
    } finally {
      setIsLoadingVideo(false);
      event.target.value = "";
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !currentScene || !game?._id || !user?.id) return;

    setIsLoadingImage(true);
    try {
      const result = await dispatch(
        loadSceneImage({
          userId: user.id,
          selectedGameId: game._id,
          scene: String(currentScene.id),
          file,
          game: { ...game, nodes, edges },
        }),
      ).unwrap();

      const savedScene = result?.nodes?.find(
        (node) => String(node.id) === String(currentScene.id),
      );
      if (!savedScene?.data?.hasImage || !savedScene.data.imagepath) {
        throw new Error("Сервер не подтвердил загрузку изображения в эту сцену");
      }

      setNodes((currentNodes) =>
        currentNodes.map((node) =>
          String(node.id) === String(currentScene.id)
            ? {
                ...node,
                data: {
                  ...node.data,
                  hasImage: true,
                  imagepath: savedScene.data.imagepath,
                  sceneMode: "image",
                },
              }
            : node,
        ),
      );
      setShowImageDialog(false);
    } catch {
      dispatch(
        addMessage({ type: "error", message: "Ошибка загрузки изображения" }),
      );
    } finally {
      setIsLoadingImage(false);
      event.target.value = "";
    }
  };

  const handleNextScene = () => {
    if (currentSceneIndex < scenes.length - 1) {
      setCurrentSceneIndex(currentSceneIndex + 1);
    }
  };

  const handlePrevScene = () => {
    if (currentSceneIndex > 0) {
      setCurrentSceneIndex(currentSceneIndex - 1);
    }
  };

  if (!currentScene) {
    return (
      <div className="mobile-editor__empty">
        <p>В сценарии пока нет сцен. Стартовая сцена создаётся автоматически.</p>
      </div>
    );
  }

  return (
    <div className="mobile-editor mobile-editor--scenario">
      <div
        className="mobile-editor__view-switch"
        role="group"
        aria-label="Разделы сценария"
      >
        <Button
          variant={activeView === "scenes" ? "contained" : "outlined"}
          aria-pressed={activeView === "scenes"}
          onClick={() => setActiveView("scenes")}
        >
          Сцены
        </Button>
        <Button
          variant={activeView === "settings" ? "contained" : "outlined"}
          startIcon={<SettingsOutlinedIcon />}
          aria-label="Настройки проекта"
          title="Настройки проекта"
          aria-pressed={activeView === "settings"}
          onClick={() => setActiveView("settings")}
        >
          Настройки
        </Button>
      </div>

      {activeView === "settings" ? (
        <Settings />
      ) : (
        <>
          {/* Header with navigation */}
          <div className="mobile-editor__header">
        <div className="mobile-editor__title">
          <h2>
            Сцена {currentSceneIndex + 1} из {scenes.length}
          </h2>
        </div>

        <div className="mobile-editor__nav">
          <IconButton
            onClick={handlePrevScene}
            disabled={currentSceneIndex === 0}
            size="small"
          >
            <ArrowBackIcon />
          </IconButton>
          <span className="mobile-editor__page">
            {currentSceneIndex + 1}/{scenes.length}
          </span>
          <IconButton
            onClick={handleNextScene}
            disabled={currentSceneIndex === scenes.length - 1}
            size="small"
          >
            <ArrowForwardIcon />
          </IconButton>
        </div>
      </div>

      {/* Scene content - no scroll */}
      <Card className="mobile-editor__card">
        <CardContent className="mobile-editor__scene-content--no-scroll">
          {/* Content mode selector */}
          <div className="mobile-editor__mode-selector">
            <label className="mobile-editor__mode-label">Формат:</label>
            <ToggleButtonGroup
              value={currentSceneMode}
              exclusive
              onChange={(e, newMode) =>
                newMode && handleContentModeChange(newMode)
              }
              fullWidth
              size="small"
            >
              <ToggleButton value="video" title="Видео">
                <VideocamOutlinedIcon />
              </ToggleButton>
              <ToggleButton value="text" title="Текст">
                <TextFieldsOutlinedIcon />
              </ToggleButton>
              <ToggleButton value="image" title="Изображение">
                <ImageOutlinedIcon />
              </ToggleButton>
            </ToggleButtonGroup>
          </div>

          {/* Media section */}
          <div className="mobile-editor__media-section">
            {currentSceneMode === "video" && (
              <>
                {currentScene?.data?.hasVideo || hasScreenshot ? (
                  <div className="mobile-editor__video-preview">
                    {hasScreenshot && imageSrc && !videoPreviewError ? (
                      <img
                        key={imageKey}
                        src={imageSrc}
                        alt={`Кадр видео для ${currentScene?.data?.label || "сцены"}`}
                        className="mobile-editor__video-screenshot"
                        onError={() => setVideoPreviewError(true)}
                      />
                    ) : (
                      <div className="mobile-editor__video-placeholder">
                        <VideocamOutlinedIcon sx={{ fontSize: 48 }} />
                        <p>
                          {videoPreviewError
                            ? "Превью видео недоступно"
                            : "Видео загружено"}
                        </p>
                      </div>
                    )}
                    <Button
                      size="small"
                      startIcon={<VideocamOutlinedIcon />}
                      onClick={() => setShowVideoDialog(true)}
                    >
                      Изменить видео
                    </Button>
                  </div>
                ) : (
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<VideocamOutlinedIcon />}
                    onClick={() => setShowVideoDialog(true)}
                    className="mobile-editor__btn-upload"
                  >
                    Загрузить видео
                  </Button>
                )}
              </>
            )}

            {currentSceneMode === "image" && (
              <>
                {currentScene?.data?.hasImage || hasImagePreview ? (
                  <div className="mobile-editor__image-preview">
                    {hasImagePreview && imagePreviewSrc && !imagePreviewError ? (
                      <img
                        key={`${imageKey}-content`}
                        src={imagePreviewSrc}
                        alt={`Изображение для ${currentScene?.data?.label || "сцены"}`}
                        className="mobile-editor__image-screenshot"
                        onError={() => setImagePreviewError(true)}
                      />
                    ) : (
                      <div className="mobile-editor__image-placeholder">
                        <ImageOutlinedIcon sx={{ fontSize: 48 }} />
                        <p>
                          {imagePreviewError
                            ? "Не удалось показать изображение"
                            : "Изображение загружено"}
                        </p>
                      </div>
                    )}
                    <Button
                      size="small"
                      startIcon={<ImageOutlinedIcon />}
                      onClick={() => setShowImageDialog(true)}
                    >
                      Изменить изображение
                    </Button>
                  </div>
                ) : (
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<ImageOutlinedIcon />}
                    onClick={() => setShowImageDialog(true)}
                    className="mobile-editor__btn-upload"
                  >
                    Загрузить изображение
                  </Button>
                )}
              </>
            )}

            {currentSceneMode === "text" && (
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Текст сцены"
                value={currentScene?.data?.sceneText || ""}
                onChange={(e) => {
                  const updatedNodes = nodes.map((node) => {
                    if (node.id === currentScene.id) {
                      return {
                        ...node,
                        data: { ...node.data, sceneText: e.target.value },
                      };
                    }
                    return node;
                  });
                  setNodes(updatedNodes);
                }}
                placeholder="Введите текст сцены"
                margin="normal"
              />
            )}
          </div>

          {/* Delete scene button */}
          {currentScene.id !== "1" && (
            <Button
              fullWidth
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlineIcon />}
              onClick={() => setShowDeleteConfirm("scene")}
              sx={{ mt: 1 }}
            >
              Удалить сцену
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Buttons section */}
      <div className="mobile-editor__buttons-section">
        <h3 className="mobile-editor__section-title">
          Варианты переходов ({sceneButtons.length}/{MAX_SCENARIO_BUTTONS})
        </h3>

        {sceneButtons.length > 0 && (
          <div className="mobile-editor__buttons-list">
            {sceneButtons.map((btn, index) => {
              const targetSceneIndex = scenes.findIndex(
                (scene) => String(scene.id) === String(btn.targetSceneId),
              );
              const isLinked = Boolean(btn.targetSceneId);
              const targetLabel =
                targetSceneIndex >= 0
                  ? `Сцена ${targetSceneIndex + 1}`
                  : btn.targetSceneLabel;

              return (
                <Card key={btn.id} className="mobile-editor__button-item">
                  <CardContent className="mobile-editor__button-content">
                    <div className="mobile-editor__button-main">
                      <div
                        className={`mobile-editor__button-text${
                          isLinked ? " mobile-editor__button-text--linked" : ""
                        }`}
                        role={isLinked ? "button" : undefined}
                        tabIndex={isLinked ? 0 : undefined}
                        aria-label={
                          isLinked
                            ? `Перейти к ${btn.targetSceneLabel} по кнопке ${btn.label}`
                            : undefined
                        }
                        onClick={() =>
                          isLinked && handleNavigateToScene(btn.targetSceneId)
                        }
                        onKeyDown={(event) => {
                          if (
                            isLinked &&
                            (event.key === "Enter" || event.key === " ")
                          ) {
                            event.preventDefault();
                            handleNavigateToScene(btn.targetSceneId);
                          }
                        }}
                      >
                        <span className="mobile-editor__button-number">
                          {index + 1}
                        </span>
                        <div className="mobile-editor__button-info">
                          <div className="mobile-editor__button-label">
                            {btn.label || "Кнопка без текста"}
                          </div>
                          <div
                            className={`mobile-editor__button-status${
                              isLinked
                                ? " mobile-editor__button-status--linked"
                                : " mobile-editor__button-status--unlinked"
                            }`}
                          >
                            {isLinked ? (
                              <LinkIcon aria-hidden="true" />
                            ) : (
                              <LinkOffIcon aria-hidden="true" />
                            )}
                            <span>
                              {isLinked
                                ? `Связана · ${targetLabel}`
                                : "НЕТ СВЯЗИ СО СЦЕНОЙ"}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="mobile-editor__button-actions">
                        <IconButton
                          size="small"
                          aria-label={`Редактировать кнопку «${btn.label}»`}
                          title="Редактировать кнопку"
                          onClick={() => {
                            setEditingButtonId(btn.buttonId);
                            setEditingButtonText(btn.label);
                            setShowEditButtonDialog(true);
                          }}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label={`Удалить кнопку «${btn.label}»`}
                          title="Удалить кнопку"
                          onClick={() => requestDeleteButton(btn.buttonId)}
                          color="error"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </div>
                    </div>
                    {!isLinked && (
                      <Button
                        fullWidth
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        className="mobile-editor__button-create-scene"
                        aria-label={`Создать следующую сцену из кнопки «${btn.label}»`}
                        onClick={() =>
                          handleCreateSceneFromButton(btn.buttonId)
                        }
                      >
                        Создать следующую сцену
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <Button
          fullWidth
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddButton}
          className="mobile-editor__btn-add-button"
        >
          Добавить кнопку
        </Button>
      </div>
        </>
      )}

      {/* Dialog: Add button */}
      <Dialog
        open={showButtonDialog}
        onClose={() => setShowButtonDialog(false)}
        fullWidth
      >
        <DialogTitle>Добавить кнопку</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={2}
            label="Текст кнопки"
            value={editingButtonText}
            onChange={(e) => setEditingButtonText(e.target.value)}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowButtonDialog(false)}>Отмена</Button>
          <Button variant="contained" onClick={handleSaveButton}>
            Добавить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Edit button */}
      <Dialog
        open={showEditButtonDialog}
        onClose={() => setShowEditButtonDialog(false)}
        fullWidth
      >
        <DialogTitle>Изменить переход</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={2}
            label="Текст кнопки"
            value={editingButtonText}
            onChange={(e) => setEditingButtonText(e.target.value)}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditButtonDialog(false)}>Отмена</Button>
          <Button variant="contained" onClick={handleSaveButton}>
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Upload video */}
      <Dialog
        open={showVideoDialog}
        onClose={() => {
          if (!isLoadingVideo) setShowVideoDialog(false);
        }}
        fullWidth
      >
        <DialogTitle>
          {isLoadingVideo ? "Загружаем видео…" : "Загрузить видео"}
        </DialogTitle>
        <DialogContent>
          {isLoadingVideo ? (
            <Alert
              severity="info"
              icon={<CircularProgress color="inherit" size={18} />}
              role="status"
              sx={{ mb: 2, alignItems: "center" }}
            >
              Видео загружается и обрабатывается. Это может занять несколько
              минут — не закрывайте окно.
            </Alert>
          ) : null}
          {videoUploadError ? (
            <Alert severity="error" role="alert" sx={{ mb: 2 }}>
              {videoUploadError}
            </Alert>
          ) : null}
          <InputFileUpload
            onChange={handleVideoUpload}
            accept="video/*"
            label={isLoadingVideo ? "Загрузка видео…" : "Загрузить видео"}
            disabled={isLoadingVideo}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowVideoDialog(false)}
            disabled={isLoadingVideo}
          >
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Upload image */}
      <Dialog
        open={showImageDialog}
        onClose={() => setShowImageDialog(false)}
        fullWidth
      >
        <DialogTitle>Загрузить изображение</DialogTitle>
        <DialogContent>
          <InputFileUpload
            onChange={handleImageUpload}
            accept="image/*"
            label="Загрузить изображение"
            disabled={isLoadingImage}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowImageDialog(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      <ScenarioButtonDeleteDialog
        deletionInfo={buttonDeleteInfo}
        onCancel={() => setButtonDeleteInfo(null)}
        onKeepScene={() =>
          buttonDeleteInfo && handleDeleteButton(buttonDeleteInfo.buttonId)
        }
        onDeleteScene={() =>
          buttonDeleteInfo &&
          handleDeleteButton(buttonDeleteInfo.buttonId, true)
        }
      />

      <ConfirmDeleteModal
        open={showDeleteConfirm === "scene"}
        title="Удалить сцену?"
        message="Сцена будет удалена вместе с её текстом, видео, скриншотами, изображениями и кнопками."
        detail="Это действие нельзя отменить."
        onClose={() => setShowDeleteConfirm(null)}
        onConfirm={handleDeleteScene}
      />
    </div>
  );
}
